// src/context/AuthContext.jsx
import React, { createContext, useContext, useEffect, useState } from "react";
import * as auth from "../services/auth";

const AuthCtx = createContext(null);

const withRole = (u) => (u ? { ...u, role: u.role ?? "MEMBER" } : null);

const isValidMembershipSubscription = (subscription) => {
  if (!subscription) return false;

  if (subscription.status === "active") {
    if (!subscription.current_period_end && !subscription.expires_at) return true;

    const activeUntil = subscription.current_period_end || subscription.expires_at;
    return activeUntil ? new Date(activeUntil) > new Date() : true;
  }

  if (subscription.status === "trialing") {
    return subscription.trial_end
      ? new Date(subscription.trial_end) > new Date()
      : false;
  }

  return false;
};

export function AuthProvider({ children }) {

  const [user, setUser] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [coreProgress, setCoreProgress] = useState([]);

  /* =========================
     REFRESH SESSION
  ========================== */
  const refreshSession = async () => {
    try {
      const fresh = await auth.me();

      if (fresh?.user) {
        setUser(withRole(fresh.user));
        setSubscriptions(fresh.subscriptions || []);
        setCoreProgress(fresh.core_progress || []);
      }
    } catch (err) {
      console.error(err);
    }
  };

  /* =========================
     BOOT (rehidratación)
  ========================== */
  useEffect(() => {
    const init = async () => {
      try {
        // 🔥 1. cargar desde localStorage (rápido)
        const raw = localStorage.getItem("zedro_session");

        if (raw) {
          const session = JSON.parse(raw);
          if (session?.user) {
            setUser(withRole(session.user));
          }
        }

        // 🔥 2. sincronizar con backend (seguro)
        const res = await auth.me();

        if (res?.ok && res.user) {
          setUser(withRole(res.user));
          setSubscriptions(res.subscriptions || []);
          setCoreProgress(res.core_progress || []);
        }

      } catch (err) {
        console.error(err);
      } finally {
        setInitialized(true);
      }
    };

    init();
  }, []);

  /* =========================
     LOGIN
  ========================== */
  const login = async (email, password) => {
    setLoading(true);

    try {
      const data = await auth.login(email, password);

      if (data?.user) {
        setUser(withRole(data.user));
      }

      const fresh = await auth.me();

      if (fresh?.user) {
        setUser(withRole(fresh.user));
        setSubscriptions(fresh.subscriptions || []);
        setCoreProgress(fresh.core_progress || []);
      }

      return data?.user;

    } finally {
      setLoading(false);
    }
  };

  /* =========================
     LOGOUT
  ========================== */
  const logout = async () => {
    try {
      await auth.logout();
    } finally {
      setUser(null);
      setSubscriptions([]);
      setCoreProgress([]);
    }
  };

  /* =========================
     UPDATE PROFILE
  ========================== */
    const updateProfileAndCompleteOnboarding = async (payload) => {
    setLoading(true);

    try {
      await auth.updateProfile(payload);

      const session = JSON.parse(localStorage.getItem("zedro_session"));

      let updatedUser = null;

      if (session?.user) {
        updatedUser = {
          ...session.user,
          name: payload.name,
          onboarding_completed: true
        };

        localStorage.setItem(
          "zedro_session",
          JSON.stringify({
            ...session,
            user: updatedUser
          })
        );

        setUser(withRole(updatedUser));
      }

      const fresh = await auth.me();

      if (fresh?.user) {
        setUser(withRole(fresh.user));
        setSubscriptions(fresh.subscriptions || []);
        setCoreProgress(fresh.core_progress || []);

        return withRole(fresh.user);
      }

      return updatedUser;

    } finally {
      setLoading(false);
    }
  };

  /* =========================
     STORAGE SYNC (multi-tab)
  ========================== */
  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key === "zedro::logout") {
        setUser(null);
        setSubscriptions([]);
        setCoreProgress([]);
      }
    };

    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

    const hasActiveSubscription =
      Array.isArray(subscriptions) &&
      subscriptions.some(isValidMembershipSubscription);

    // 🔥 IMPORTANTE: no bloquear render
    if (!initialized) return null;

  return (
    <AuthCtx.Provider
      value={{
        user,
        subscriptions,
        hasActiveSubscription,
        coreProgress,
        loading,
        initialized,
        refreshSession,
        login,
        logout,
        updateProfileAndCompleteOnboarding,
      }}
    >
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);